import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  api,
  setAccessToken,
  setAuthFailureHandler,
  setRefreshMode,
  triggerRefresh,
} from '@/shared/lib/axios';
import { getDeviceInput } from '@/shared/utils/device';

import { ME_QUERY_KEY } from './hooks/use-me';
import { guestAuthResponseSchema, loginResponseSchema, meResponseSchema, type Guest, type User } from './types';
import { clearAuthMode, getAuthMode, setAuthMode } from './utils/auth-mode';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface VerifyEmailCredentials {
  email: string;
  code: string;
}

export interface AppleSignInPayload {
  idToken: string;
  // Apple sends name/email only on the first authorization.
  user?: { email?: string; name?: { firstName?: string; lastName?: string } };
}

interface AuthContextValue {
  user: User | null;
  /** Convenience flag — true when `user` is an anonymous guest session. */
  isGuest: boolean;
  isLoading: boolean;
  login: (req: LoginCredentials) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginWithApple: (payload: AppleSignInPayload) => Promise<void>;
  verifyEmail: (req: VerifyEmailCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

// A guest doc has no email or display name. We synthesise a User-shaped object
// so the rest of the app (ProtectedRoute, the chat sidebar) treats a guest like
// any signed-in user — `isGuest` is the only thing that distinguishes it.
function buildGuestUser(guest: Guest): User {
  return {
    id: guest.id,
    email: '',
    displayName: 'Guest',
    authMethods: ['guest'],
    isGuest: true,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  // Bootstrap is always-on now: on mount we try to refresh + fetch /me. While
  // that's in flight `isLoading` keeps ProtectedRoute from flashing the login
  // page for users who actually have a valid refresh cookie.
  const [isLoading, setIsLoading] = useState(true);

  // `logout` needs to know whether the live session is a guest, without taking
  // `user` as a dependency (which would churn its identity on every render).
  const isGuestRef = useRef(false);
  isGuestRef.current = user?.isGuest === true;

  const login = useCallback(
    async (req: LoginCredentials) => {
      // Captured before the response wipes the local auth-mode flag — the
      // `guest_id` cookie is httpOnly so this is the only signal we have that
      // the caller was a guest a moment ago. Used below to migrate the guest's
      // chat history into the just-signed-in account.
      const wasGuest = getAuthMode() === 'guest';

      const res = await api.post('/api/auth/login', { ...req, device: getDeviceInput() });
      const parsed = loginResponseSchema.parse(res.data);
      setAccessToken(
        parsed.tokens.accessToken,
        new Date(parsed.tokens.accessTokenExpiresAt),
      );
      // A previous guest session on this device would otherwise leave the
      // refresh routing pointed at /api/guest/*.
      setRefreshMode('user');
      clearAuthMode();
      // Seed the TanStack Query cache so `useMe()` returns instantly after redirect.
      queryClient.setQueryData(ME_QUERY_KEY, parsed.user);
      setUser(parsed.user);

      if (wasGuest) {
        // Server-side migration: api/ reads the `guest_id` cookie, reassigns
        // the guest's conversations to req.user, marks the guest converted,
        // and clears the cookie. Idempotent — a missing/already-converted
        // guest just returns 0. Failures are non-fatal: the user is signed in.
        try {
          await api.post('/api/guest/merge-to-current-user');
        } catch {
          // Migration failure shouldn't block the just-completed login. The
          // server logs it; the unclaimed guest history will be pruned by the
          // cleanup job eventually.
        }
        // Drop the guest's cached chat data so the freshly migrated
        // conversations show up under the now-signed-in user.
        void queryClient.invalidateQueries();
      }
    },
    [queryClient],
  );

  const loginAsGuest = useCallback(async () => {
    const res = await api.post('/api/guest/auth/init', { device: getDeviceInput() });
    const parsed = guestAuthResponseSchema.parse(res.data);
    setAccessToken(parsed.accessToken, new Date(parsed.accessTokenExpiresAt));
    // Subsequent token rotation (proactive + 401-retry) must hit /api/guest/*,
    // and the bootstrap after a reload needs to know this is a guest session.
    setRefreshMode('guest');
    setAuthMode('guest');
    const guestUser = buildGuestUser(parsed.guest);
    queryClient.setQueryData(ME_QUERY_KEY, guestUser);
    setUser(guestUser);
  }, [queryClient]);

  // Shared tail for the social-login flows (Google/Apple). Mirrors `login`:
  // posts the provider token, stores the session, and migrates a prior guest's
  // chat history into the freshly signed-in account.
  const socialLogin = useCallback(
    async (endpoint: string, body: Record<string, unknown>) => {
      const wasGuest = getAuthMode() === 'guest';

      const res = await api.post(endpoint, { ...body, device: getDeviceInput() });
      const parsed = loginResponseSchema.parse(res.data);
      setAccessToken(parsed.tokens.accessToken, new Date(parsed.tokens.accessTokenExpiresAt));
      setRefreshMode('user');
      clearAuthMode();
      queryClient.setQueryData(ME_QUERY_KEY, parsed.user);
      setUser(parsed.user);

      if (wasGuest) {
        try {
          await api.post('/api/guest/merge-to-current-user');
        } catch {
          // Non-fatal — same rationale as `login`'s guest migration.
        }
        void queryClient.invalidateQueries();
      }
    },
    [queryClient],
  );

  const loginWithGoogle = useCallback(
    (idToken: string) => socialLogin('/api/auth/google', { idToken }),
    [socialLogin],
  );

  const loginWithApple = useCallback(
    (payload: AppleSignInPayload) =>
      socialLogin('/api/auth/apple', {
        idToken: payload.idToken,
        ...(payload.user ? { user: payload.user } : {}),
      }),
    [socialLogin],
  );

  const verifyEmail = useCallback(
    async (req: VerifyEmailCredentials) => {
      const res = await api.post('/api/auth/verify', { ...req, device: getDeviceInput() });
      const parsed = loginResponseSchema.parse(res.data);
      setAccessToken(
        parsed.tokens.accessToken,
        new Date(parsed.tokens.accessTokenExpiresAt),
      );
      setRefreshMode('user');
      clearAuthMode();
      queryClient.setQueryData(ME_QUERY_KEY, parsed.user);
      setUser(parsed.user);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    const endpoint = isGuestRef.current ? '/api/guest/auth/logout' : '/api/auth/logout';
    try {
      await api.post(endpoint);
    } catch {
      // Even if the server-side logout call fails (network, already-expired cookie),
      // we still clear local state — the user clicked "logout".
    } finally {
      setAccessToken(null);
      setRefreshMode('user');
      clearAuthMode();
      setUser(null);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
    }
  }, [queryClient]);

  // Bootstrap: try to mint a fresh access token from the httpOnly refresh
  // cookie, then fetch /me. If the cookie is missing or expired, refresh 401s
  // → catch sets user=null and the protected routes redirect to /login.
  // Otherwise the user stays signed in across reloads until the refresh-token
  // cookie itself expires. Guest sessions (flagged in localStorage) bootstrap
  // against /api/guest/* instead.
  useEffect(() => {
    let cancelled = false;

    const bootstrapGuest = async () => {
      setRefreshMode('guest');
      try {
        const res = await api.post('/api/guest/auth/refresh', { device: getDeviceInput() });
        const parsed = guestAuthResponseSchema.parse(res.data);
        setAccessToken(parsed.accessToken, new Date(parsed.accessTokenExpiresAt));
        if (!cancelled) {
          const guestUser = buildGuestUser(parsed.guest);
          queryClient.setQueryData(ME_QUERY_KEY, guestUser);
          setUser(guestUser);
        }
      } catch {
        // Guest refresh cookie gone/expired — drop back to an anonymous,
        // logged-out state so the user can start a fresh guest session.
        if (!cancelled) {
          setRefreshMode('user');
          clearAuthMode();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    const bootstrapUser = async () => {
      try {
        await triggerRefresh();
      } catch {
        if (!cancelled) {
          setUser(null);
          setIsLoading(false);
        }
        return;
      }
      try {
        const res = await api.get('/api/auth/me');
        const parsed = meResponseSchema.parse(res.data).user;
        if (!cancelled) {
          queryClient.setQueryData(ME_QUERY_KEY, parsed);
          setUser(parsed);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void (getAuthMode() === 'guest' ? bootstrapGuest() : bootstrapUser());
    return () => {
      cancelled = true;
    };
  }, [queryClient]);

  // Wire axios's auth-failure callback so a failed refresh clears React state.
  useEffect(() => {
    setAuthFailureHandler(() => {
      setRefreshMode('user');
      clearAuthMode();
      setUser(null);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
    });
    return () => setAuthFailureHandler(null);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isGuest: user?.isGuest === true,
      isLoading,
      login,
      loginAsGuest,
      loginWithGoogle,
      loginWithApple,
      verifyEmail,
      logout,
    }),
    [user, isLoading, login, loginAsGuest, loginWithGoogle, loginWithApple, verifyEmail, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
